/**
 * Aree territoriali Fastweb e mappatura provincia → area.
 *
 * Le 5 aree partizionano tutte le province italiane (fonte: file committente
 * "Abilitazioni Dashaboard ETO.xlsx" + correzioni confermate: PV/LO/MB in NW1,
 * AT in NW2). Usate per:
 *  - visibilita' territoriale: un utente Fastweb non-ETO vede solo le pratiche
 *    la cui provincia ricade nelle sue `aree` (profilo utente);
 *  - filtro destinatari email (Cloud Function onDocCaricati).
 *
 * ⚠️ TENERE IN SYNC con functions/territorio.js (mirror CommonJS: le Cloud
 * Functions non possono importare da frontend/src).
 */

export const AREE = ['centro', 'ne', 'nw1', 'nw2', 'sud'] as const;
export type Area = typeof AREE[number];

export const AREA_LABELS: Record<Area, string> = {
  centro: 'Centro',
  ne: 'Nord-Est',
  nw1: 'Nord-Ovest 1 (Lombardia)',
  nw2: 'Nord-Ovest 2 (Piemonte/Liguria/VdA)',
  sud: 'Sud',
};

/**
 * Composizione regionale delle aree (documentazione, non usata a runtime):
 *  - centro: Toscana, Marche, Abruzzo, Molise, Umbria, Lazio, Sardegna
 *  - ne:     Friuli-Venezia Giulia, Trentino-Alto Adige, Veneto, Emilia-Romagna
 *  - nw1:    Lombardia
 *  - nw2:    Piemonte, Liguria, Valle d'Aosta
 *  - sud:    Calabria, Sicilia, Puglia, Basilicata, Campania
 */
export const PROVINCIA_TO_AREA: Record<string, Area> = {
  // nw1 — Lombardia (incluse PV, LO, MB: assenti nel file per svista, confermate dal committente)
  BG: 'nw1', BS: 'nw1', CO: 'nw1', CR: 'nw1', LC: 'nw1', LO: 'nw1',
  MB: 'nw1', MI: 'nw1', MN: 'nw1', PV: 'nw1', SO: 'nw1', VA: 'nw1',
  // nw2 — Piemonte (incluso AT: assente nel file per svista, confermato dal committente)
  AL: 'nw2', AT: 'nw2', BI: 'nw2', CN: 'nw2', NO: 'nw2', TO: 'nw2', VB: 'nw2', VC: 'nw2',
  // nw2 — Liguria
  GE: 'nw2', IM: 'nw2', SP: 'nw2', SV: 'nw2',
  // nw2 — Valle d'Aosta
  AO: 'nw2',
  // ne — Friuli-Venezia Giulia
  GO: 'ne', PN: 'ne', TS: 'ne', UD: 'ne',
  // ne — Trentino-Alto Adige
  BZ: 'ne', TN: 'ne',
  // ne — Veneto
  BL: 'ne', PD: 'ne', RO: 'ne', TV: 'ne', VE: 'ne', VI: 'ne', VR: 'ne',
  // ne — Emilia-Romagna
  BO: 'ne', FC: 'ne', FE: 'ne', MO: 'ne', PC: 'ne', PR: 'ne', RA: 'ne', RE: 'ne', RN: 'ne',
  // centro — Toscana
  AR: 'centro', FI: 'centro', GR: 'centro', LI: 'centro', LU: 'centro',
  MS: 'centro', PI: 'centro', PO: 'centro', PT: 'centro', SI: 'centro',
  // centro — Marche
  AN: 'centro', AP: 'centro', FM: 'centro', MC: 'centro', PU: 'centro',
  // centro — Abruzzo
  AQ: 'centro', CH: 'centro', PE: 'centro', TE: 'centro',
  // centro — Molise
  CB: 'centro', IS: 'centro',
  // centro — Umbria
  PG: 'centro', TR: 'centro',
  // centro — Lazio
  FR: 'centro', LT: 'centro', RI: 'centro', RM: 'centro', VT: 'centro',
  // centro — Sardegna (incluse sigle storiche pre-2016 per tolleranza dati legacy)
  CA: 'centro', NU: 'centro', OR: 'centro', SS: 'centro', SU: 'centro',
  CI: 'centro', VS: 'centro', OT: 'centro', OG: 'centro',
  // sud — Calabria
  CS: 'sud', CZ: 'sud', KR: 'sud', RC: 'sud', VV: 'sud',
  // sud — Sicilia
  AG: 'sud', CL: 'sud', CT: 'sud', EN: 'sud', ME: 'sud', PA: 'sud', RG: 'sud', SR: 'sud', TP: 'sud',
  // sud — Puglia
  BA: 'sud', BR: 'sud', BT: 'sud', FG: 'sud', LE: 'sud', TA: 'sud',
  // sud — Basilicata
  MT: 'sud', PZ: 'sud',
  // sud — Campania
  AV: 'sud', BN: 'sud', CE: 'sud', NA: 'sud', SA: 'sud',
};

/**
 * Sigle provincia soppresse (Sardegna, riordino 2016): presenti nella mappa solo
 * per tolleranza sui dati legacy in lettura — NON vanno offerte per l'inserimento
 * di nuove pratiche.
 */
const SIGLE_STORICHE = new Set(['CI', 'VS', 'OT', 'OG']);

/**
 * Sigle provincia correnti, ordinate alfabeticamente — alimenta il dropdown
 * Provincia nel form Nuova Attività (PracticeForm).
 */
export const SIGLE_PROVINCE: string[] = Object.keys(PROVINCIA_TO_AREA)
  .filter((s) => !SIGLE_STORICHE.has(s))
  .sort();

/** Profilo utente ai fini territoriali (sottoinsieme di /users/{uid}). */
export interface TerritorialProfile {
  role?: string | null;
  isEto?: boolean | null;
  aree?: string[] | null;
}

export interface PracticeWithProvincia {
  provincia?: string | null;
}

/** Area di competenza per una sigla provincia (case-insensitive). Null se sconosciuta. */
export function getAreaForProvincia(sigla: string | null | undefined): Area | null {
  if (!sigla || typeof sigla !== 'string') return null;
  return PROVINCIA_TO_AREA[sigla.trim().toUpperCase()] ?? null;
}

/** Profilo ETO = utente Fastweb in scrittura, visibilita' nazionale. */
export function isEtoProfile(profile: TerritorialProfile | null | undefined): boolean {
  return profile?.isEto === true;
}

/**
 * Visibilita' territoriale di una pratica per un profilo utente.
 *  - Ruoli non-Fastweb: sempre true (hanno i loro filtri: studio, ditta, rules).
 *  - Fastweb ETO: sempre true (visibilita' nazionale).
 *  - Fastweb senza `aree` (e non ETO): true — fallback transizione, comportamento
 *    attuale finche' il profilo non viene classificato.
 *  - Fastweb territoriale: true solo se l'area della provincia e' tra le sue.
 *    Provincia assente o non mappata → false (la vedono solo gli ETO).
 */
export function canSeePractice(
  profile: TerritorialProfile | null | undefined,
  practice: PracticeWithProvincia | null | undefined
): boolean {
  if (!profile || profile.role !== 'fastweb') return true;
  if (isEtoProfile(profile)) return true;
  const aree = profile.aree;
  if (!Array.isArray(aree) || aree.length === 0) return true;
  const area = getAreaForProvincia(practice?.provincia);
  return area != null && aree.includes(area);
}

/** Valida un array di aree (per form invito/modifica utente). */
export function isValidAree(aree: unknown): aree is Area[] {
  return Array.isArray(aree) && aree.length > 0 && aree.every((a) => (AREE as readonly string[]).includes(a as string));
}
